import React from "react";
import { Link } from "react-router-dom";
import TrustBand from "../../components/TrustBand/TrustBand";
import "./Products.css";

const IOS_URL = "https://apps.apple.com/us/app/range-pocket/id6733222657";
const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.armorysync.rangepocket";
const RANGEGRID_URL = "https://rangegrid.app/";

const armorySyncProducts = [
  {
    name: "Range Pocket",
    audience: "For shooters and firearm owners",
    body: "Track firearms, ammo, maintenance, range visits, training notes, target photos, and range-day history in one organized app.",
    status: "Available now",
    media: "image",
    image: "/features/feature-armory-status.webp",
    imageAlt: "Range Pocket armory status dashboard",
    actions: [
      { label: "App Store", href: IOS_URL, primary: true },
      { label: "Google Play", href: ANDROID_URL },
      { label: "Product details", to: "/range-pocket" },
    ],
  },
  {
    name: "RangeGrid",
    audience: "For range owners and operators",
    body: "Run memberships, reservations, events, payments, public websites, staff access, and member self-service from one connected platform.",
    status: "Owner platform",
    media: "rangegrid",
    image: "/features/rangegrid-owner-command-preview.png",
    imageAlt: "RangeGrid owner command workflow preview",
    workflow: [
      "Member applications",
      "Resource reservations",
      "Programs and events",
      "Payments and websites",
    ],
    actions: [
      { label: "Explore RangeGrid", href: RANGEGRID_URL, primary: true },
    ],
  },
];

const productCapabilities = [
  {
    title: "Firearm inventory",
    body: "Track firearms, ammo, accessories, photos, purchase details, and configurations without managing a spreadsheet.",
    href: "/firearm-inventory-app",
  },
  {
    title: "Range visit logging",
    body: "Capture range, firearms used, round counts, target photos, notes, conditions, and training context.",
    href: "/range-log-app",
  },
  {
    title: "Maintenance and reminders",
    body: "Connect maintenance notes, accessory changes, battery reminders, and round counts to real use.",
    href: "/firearm-maintenance-tracker",
  },
];

const productFit = [
  "Firearm owners organizing inventory and range history",
  "Shooters who want continuity between range sessions",
  "Shooters building repeatable training habits",
  "Owners who prefer clear records they can review later",
];

function Products() {
  return (
    <main className="products-page">
      <header className="products-hero">
        <div>
          <div className="products-kicker">ArmorySync products</div>
          <h1>Tools for both sides of the range experience.</h1>
        </div>
        <div className="products-hero-side">
          <p>
            Range Pocket helps shooters keep better records. RangeGrid helps range
            owners run memberships, reservations, events, payments, and member
            self-service from one operating platform.
          </p>
          <div className="products-hero-actions">
            <Link className="product-button product-button-secondary" to="/range-pocket">
              Range Pocket
            </Link>
            <a className="product-button product-button-primary" href={RANGEGRID_URL}>
              RangeGrid
            </a>
            <a className="product-button product-button-secondary" href={IOS_URL}>
              App Store
            </a>
          </div>
          <p className="product-support-note">
            Questions before downloading? Email <a href="mailto:support@armorysync.com">support@armorysync.com</a>.
          </p>
        </div>
      </header>

      <section className="product-family" aria-label="ArmorySync products">
        {armorySyncProducts.map((product) => (
          <article className={`product-family-card product-family-card-${product.media}`} key={product.name}>
            <div className="product-label-row">
              <span>{product.status}</span>
              <span>{product.audience}</span>
            </div>
            <div className="product-family-body">
              <div>
                <h2>{product.name}</h2>
                <p>{product.body}</p>
              </div>
              {product.image ? (
                <div className="product-card-shot">
                  <img src={product.image} alt={product.imageAlt} />
                </div>
              ) : null}
              {product.workflow ? (
                <div className="product-workflow-list" aria-label="RangeGrid workflows">
                  {product.workflow.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="product-actions">
              {product.actions.map((action) => (
                action.to ? (
                  <Link
                    className={`product-button ${action.primary ? "product-button-primary" : "product-button-secondary"}`}
                    key={action.label}
                    to={action.to}
                  >
                    {action.label}
                  </Link>
                ) : (
                  <a
                    className={`product-button ${action.primary ? "product-button-primary" : "product-button-secondary"}`}
                    href={action.href}
                    key={action.label}
                  >
                    {action.label}
                  </a>
                )
              ))}
            </div>
          </article>
        ))}
      </section>

      <TrustBand className="products-trust-band" />

      <section className="product-capabilities" aria-label="Range Pocket capabilities">
        {productCapabilities.map((capability) => (
          <article key={capability.title}>
            <h2>{capability.title}</h2>
            <p>{capability.body}</p>
            <Link to={capability.href}>Learn more</Link>
          </article>
        ))}
      </section>

      <section className="product-fit" aria-labelledby="product-fit-title">
        <div>
          <div className="products-kicker">Built for</div>
          <h2 id="product-fit-title">People who need records they can actually use.</h2>
        </div>
        <ul>
          {productFit.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
    </main>
  );
}

export const Products_Page = {
  path: "/products",
  element: <Products />,
};

export default Products;
